'use client';
import { createContext, useMemo } from 'react';
import { FlowType, Requirements, FlowMap, StepParams } from '../types';

import { pcFlowConfig } from '../flow-config/pc';
import { printerFlowConfig } from '../flow-config/printer';
import { instantInkFlowConfig } from '../flow-config/instant_ink';
import { useRouter } from 'next/navigation';
import { buildPath } from '@storefront/utils/utils';
import DataCollection from '@storefront/services/DataCollection';

interface RedirectToStepParams {
  stepKey?: string;
  path?: string;
  params?: Record<string, string>;
  shouldUseRouter?: boolean;
}

interface GoToStepOptions {
  path?: string;
  params?: StepParams[keyof StepParams];
  requirements?: Partial<Requirements>;
  shouldUseRouter?: boolean;
}

export interface IFlowOrchestrator {
  currentStep: string;
  flowType: FlowType;
  isSMB: boolean;
  goToNextStep?: ({ params, requirements }: GoToStepOptions) => void;
  goToPreviousStep?: ({
    path,
    params,
    requirements,
    shouldUseRouter,
  }: GoToStepOptions) => void;
}

export const FlowOrchestratorContext = createContext<
  IFlowOrchestrator | undefined
>(undefined);

interface FlowOrchestratorProviderProps {
  flowType: FlowType;
  currentStep: string;
  isSMB: boolean;
  children: React.ReactNode;
}

const flowConfigs: FlowMap = {
  hybrid: pcFlowConfig,
  bundleSelection: printerFlowConfig,
  instantInk: instantInkFlowConfig,
  unknown: {},
};

export const FlowOrchestratorProvider = (
  props: FlowOrchestratorProviderProps
) => {
  const { children, flowType, currentStep, isSMB } = props;
  const router = useRouter();

  // Set synchronously during render so it's available before any child effects fire
  DataCollection.setIsSMB(isSMB);

  const goToNextStep = ({
    params,
    requirements = {},
  }: GoToStepOptions = {}) => {
    const nextKey = getRelativeStepKey(1, requirements);
    if (nextKey)
      redirectToStep({
        stepKey: nextKey,
        params,
      });
  };

  const redirectToStep = ({
    stepKey,
    path,
    params,
    shouldUseRouter = true,
  }: RedirectToStepParams) => {
    let targetPath = path;
    let stepConfig;

    if (
      !targetPath &&
      stepKey &&
      (flowType === FlowType.Pc ||
        flowType === FlowType.Printer ||
        flowType === FlowType.InstantInk)
    ) {
      targetPath = flowConfigs[flowType][stepKey]?.path;
      stepConfig = flowConfigs[flowType][stepKey];
    }

    if (targetPath) {
      if (params && Object.keys(params).length > 0) {
        const stringParams: Record<string, string> = {};
        Object.entries(params).forEach(([key, value]) => {
          stringParams[key] = String(value);
        });
        const searchParams = new URLSearchParams(stringParams).toString();
        targetPath += `?${searchParams}`;
      }
      const finalPath = stepConfig?.relativePath
        ? targetPath
        : buildPath(targetPath);

      if (shouldUseRouter) {
        router.push(finalPath);
      } else {
        window.location.href = finalPath;
      }
    }
  };

  const goToPreviousStep = ({
    path,
    params,
    requirements = {},
    shouldUseRouter = true,
  }: GoToStepOptions = {}) => {
    if (path) {
      redirectToStep({
        path,
        params,
        shouldUseRouter,
      });
      return;
    }

    const prevKey = getRelativeStepKey(-1, requirements);
    if (prevKey)
      redirectToStep({
        stepKey: prevKey,
        params,
        shouldUseRouter,
      });
  };

  const getRelativeStepKey = (
    offset: number,
    requirementsObj: Partial<Requirements> = {}
  ): string | null => {
    const stepEntries = Object.entries(flowConfigs[flowType]);

    // Find the index of the current step in the flow
    const currentStepIndex = stepEntries.findIndex(
      ([stepKey]) => stepKey === currentStep
    );

    // Next (or previous) step based on the offset
    let nextStepIndex = currentStepIndex + offset;

    // This loop is necessary to handle cases where the next step is not allowed based on conditions
    while (nextStepIndex >= 0 && nextStepIndex < stepEntries.length) {
      const [nextStepKey, nextStepConfig] = stepEntries[nextStepIndex];

      // Validate if the next step meets the requirements
      const isValid =
        !nextStepConfig.requirements?.length ||
        nextStepConfig.requirements.every(
          (req) => requirementsObj[req] === true
        );

      if (isValid) {
        return nextStepKey;
      }
      nextStepIndex += offset;
    }
    return null;
  };

  const contextValue = useMemo(
    () => ({
      currentStep,
      flowType,
      isSMB,
      goToNextStep,
      goToPreviousStep,
    }),
    [currentStep, flowType, goToNextStep, goToPreviousStep]
  );

  return (
    <FlowOrchestratorContext.Provider value={contextValue}>
      {children}
    </FlowOrchestratorContext.Provider>
  );
};
